import React, { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck, X, RefreshCw, Plus, Users } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const statusStyle = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  expired: "bg-amber-50 text-amber-700 border-amber-200",
};

const AdminUserMemberships = () => {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [granting, setGranting] = useState(false);
  const [grant, setGrant] = useState({ user_id: "", tier_id: "" });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([
        api.get("/admin/user-memberships", { withCredentials: true }),
        api.get("/admin/memberships", { withCredentials: true }),
      ]);
      setItems(a.data);
      setTiers(t.data);
    } catch {
      toast.error("Could not load user memberships.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this membership immediately? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/user-memberships/${id}`, { withCredentials: true });
      toast.success("Revoked.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed.");
    }
  };

  const submitGrant = async (e) => {
    e.preventDefault();
    if (!grant.user_id || !grant.tier_id) { toast.error("user_id and tier are required."); return; }
    setGranting(true);
    try {
      await api.post("/admin/user-memberships", grant, { withCredentials: true });
      toast.success("Granted.");
      setGrant({ user_id: "", tier_id: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed.");
    } finally {
      setGranting(false);
    }
  };

  const active = items.filter((m) => m.status === "active");
  const inactive = items.filter((m) => m.status !== "active");

  return (
    <div className="space-y-5" data-testid="admin-user-memberships">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-spike-blue" /> User memberships
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Who's on which plan, plus manually grant or revoke memberships.
          </p>
        </div>
        <button type="button" data-testid="user-memberships-refresh" onClick={reload} className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium px-3 py-2 rounded-md">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Grant new membership form */}
      <form onSubmit={submitGrant} className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end" data-testid="grant-form">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1.5">User ID</label>
          <input
            data-testid="grant-user-id"
            className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm"
            value={grant.user_id}
            onChange={(e) => setGrant({ ...grant, user_id: e.target.value })}
            placeholder="e.g. user_6cb322efda0a"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1.5">Tier</label>
          <select
            data-testid="grant-tier-id"
            className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm"
            value={grant.tier_id}
            onChange={(e) => setGrant({ ...grant, tier_id: e.target.value })}
          >
            <option value="">— Select —</option>
            {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} · ${t.price_cad}/{t.period}</option>)}
          </select>
        </div>
        <button
          type="submit"
          data-testid="grant-submit"
          disabled={granting}
          className="inline-flex items-center justify-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-md"
        >
          {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Grant
        </button>
      </form>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>
      ) : (
        <>
          {/* Active memberships */}
          <div>
            <div className="text-xs font-orbitron uppercase tracking-[0.25em] text-slate-500 mb-2">Active · {active.length}</div>
            {active.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-500 text-sm">No active memberships yet.</div>
            ) : (
              <div className="space-y-2">
                {active.map((m) => (
                  <div key={m.id} data-testid={`um-row-${m.id}`} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-4 justify-between">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-spike-blue text-white flex items-center justify-center font-bold">
                        {m.user?.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                          {m.user?.name || "Unknown"}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${statusStyle[m.status]}`}>
                            {m.status}
                          </span>
                          {m.cancel_at_period_end && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              ending {new Date(m.current_period_end).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{m.user?.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-slate-900 flex items-center gap-2 justify-end">
                        <ShieldCheck className="w-4 h-4 text-spike-blue" /> {m.tier?.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        Renews {new Date(m.current_period_end).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={`um-revoke-${m.id}`}
                      onClick={() => revoke(m.id)}
                      className="p-2 text-slate-600 hover:text-red-500 rounded"
                      title="Revoke immediately"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Inactive history */}
          {inactive.length > 0 && (
            <div>
              <div className="text-xs font-orbitron uppercase tracking-[0.25em] text-slate-500 mb-2">History · {inactive.length}</div>
              <div className="space-y-2">
                {inactive.map((m) => (
                  <div key={m.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 text-sm text-slate-600">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${statusStyle[m.status]}`}>
                      {m.status}
                    </span>
                    <span className="text-slate-900 font-medium">{m.user?.name || "Unknown"}</span>
                    <span className="text-slate-500">·</span>
                    <span>{m.tier?.name}</span>
                    <span className="ml-auto text-xs text-slate-400">
                      {new Date(m.started_at).toLocaleDateString()} → {new Date(m.current_period_end).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminUserMemberships;
